import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { ApiClient } from "@/lib/api/client";
import type { GeoPlace } from "@/lib/api/models/birth";
import { errorMessage } from "@/lib/api/errors";

/** Below this many characters nothing is searched. */
export const PLACE_QUERY_MIN = 2;
const DEBOUNCE_MS = 250;

/** A value that settles only after the person stops typing for a beat. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

export interface PlaceSearch {
  readonly results: readonly GeoPlace[];
  readonly searching: boolean;
  readonly error: string | null;
  /** The search for exactly what is typed now has come back, with results or not. */
  readonly answered: boolean;
}

/**
 * Typeahead over `GET /geocode`. Below two characters the backend returns
 * nothing, so nothing is asked. Results are cached per query so backspacing
 * over a name does not re-fetch what was just shown.
 */
export function usePlaceSearch(api: ApiClient, query: string): PlaceSearch {
  const typed = query.trim();
  const settled = useDebouncedValue(typed, DEBOUNCE_MS);
  const enabled = settled.length >= PLACE_QUERY_MIN;
  const search = useQuery({
    queryKey: ["geocode", settled],
    queryFn: () => api.geocode(settled),
    enabled,
    staleTime: 5 * 60_000,
  });
  return {
    results: enabled ? (search.data ?? []) : [],
    searching: enabled && search.isFetching,
    error: search.error ? errorMessage(search.error) : null,
    answered: enabled && settled === typed && !search.isFetching && (search.isSuccess || search.isError),
  };
}
